import { css } from "@emotion/css";
import { GroupBase, StylesConfig } from "react-select";

import { grey, white } from "@/modules/theme/utils/colors";
import { Theme } from "@/modules/theme/utils/createTheme";

import { BasicOption } from "../SelectInput";

export const createSelectInputStyles = ({ spacing }: Theme) => {
  return {
    label: css({
      paddingLeft: spacing(1),
    }),
    error: css({
      lineHeight: "normal",
    }),
  };
};

interface PropsStyles {
  theme: Theme;
  state: {
    error: boolean;
  };
}

type ReactStylesConstructor = <
  Option extends BasicOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: PropsStyles
) => StylesConfig<Option, IsMulti, Group>;

export const createReactSelectStyles: ReactStylesConstructor = ({
  theme: { borders, palette, typography, boxShadows, spacing },
  state: { error },
}) => {
  return {
    menu: (provided) => ({
      ...provided,
      marginTop: spacing(-1.25),
      boxShadow: boxShadows.dropdown,
    }),
    valueContainer: (provided) => ({
      ...provided,
      lineHeight: "normal",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    control: (provided, { isFocused }) => ({
      ...provided,
      minHeight: spacing(5),
      color: palette.secondary.textDark,
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeight.medium,
      fontSize: typography.pxToRem(13.3),
      cursor: "pointer",
      border: isFocused
        ? borders.input.main
        : error
        ? borders.input.error
        : "1px solid transparent",
      boxShadow: "none",
      borderRadius: 2,
      backgroundColor: error ? palette.error.light : grey[100],
      "&:hover": {
        border: isFocused ? borders.input.main : "1px solid transparent",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      fontWeight: typography.fontWeight.medium,
      color: error ? palette.error.textDark : grey[500],
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      padding: 0,
    }),

    dropdownIndicator: (provided) => ({
      ...provided,
      padding: spacing(0.625),
      marginRight: spacing(0.625),
      color: palette.default.text,
      "&:hover": {
        color: "inherit",
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: spacing(0.5),
    }),
    option: (provided, { isSelected, isFocused }) => ({
      ...provided,
      position: "relative",
      padding: spacing([1.5, 2]),
      backgroundColor: isSelected || isFocused ? grey[100] : white,
      color: palette.secondary.textDark,
      fontWeight: typography.fontWeight.medium,
      cursor: "pointer",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      paddingRight: spacing(5),
      "&:hover, &:active, &:focus": {
        backgroundColor: isSelected ? palette.secondary.main : grey[0],
      },
      "&:after": {
        content: '""',
        position: "absolute",
        width: spacing(1),
        height: spacing(0.75),
        right: spacing(2),
        top: "calc(50% - 3px)",
        transform: "translateY(-50%) rotate(-45deg)",
        display: isSelected ? "block" : "none",
        borderBottom: `2px solid ${palette.tertiary.text}`,
        borderLeft: `2px solid ${palette.tertiary.text}`,
      },
    }),
  };
};
